// src/components/Garaj.js

import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, query, onSnapshot, doc, updateDoc, where, serverTimestamp } from 'firebase/firestore';
import { Box, CircularProgress } from '@mui/material';
import StatusColumn from './StatusColumn';
import { useReactToPrint } from 'react-to-print';
import { TeslimFormu } from './TeslimFormu';
import { TeslimNotlariModal } from './TeslimAkisModallar';
import { closeLinkedAppointments } from './appointmentSync';

const Garaj = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderToPrint, setOrderToPrint] = useState(null);
  const printRef = useRef();

  // Teslim akış state'leri (K3: akış tek adım — yalnız teslim notları)
  const [teslimAkisAcik, setTeslimAkisAcik] = useState(false);
  const [secilenIsEmri, setSecilenIsEmri] = useState(null);

  const statuses = ["İşlemde", "Tamamlandı", "Teslime Hazır"];

  // react-to-print v3 `contentRef` bekler; v2'deki `content: () => ref.current` sessizce
  // devre dışı kalıyordu ("did not receive a `contentRef`" + "There is nothing to print")
  // → teslim formu HİÇ yazdırılmıyordu (Faz 8 uçtan uca testinde yakalandı).
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => {
      setOrderToPrint(null);
    }
  });

  useEffect(() => {
    if (orderToPrint) {
      handlePrint();
    }
  }, [orderToPrint, handlePrint]);

  useEffect(() => {
    const q = query(collection(db, "workOrders"), where("durum", "in", statuses));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setWorkOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("İş emirleri çekilirken hata oluştu: ", error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (workOrderId, newStatus) => {
    const workOrderRef = doc(db, "workOrders", workOrderId);
    try {
      await updateDoc(workOrderRef, { durum: newStatus });
      if (newStatus === 'Tamamlandı') {
        const order = workOrders.find(wo => wo.id === workOrderId);
        try {
          await closeLinkedAppointments(workOrderId, order?.appointmentId);
        } catch (randevuHata) {
          console.error("Bağlı randevu kapatılamadı:", randevuHata);
        }
      }
    } catch (e) {
      console.error("Durum güncellenirken hata oluştu: ", e);
    }
  };

  // ADIM 1: Teslim Et butonuna basıldığında - Teslim Notları Modalı aç
  const handleMarkAsDelivered = (workOrderId) => {
    const order = workOrders.find(wo => wo.id === workOrderId);
    if (order) {
      setSecilenIsEmri(order);
      setTeslimAkisAcik(true);
    }
  };

  // ADIM 2 (son adım): Teslim notları girildi - teslimi tamamla.
  // K2/K3: Garanti kartı adımı kaldırıldı; `warranties` yazımı ve `garantiKartiNo`
  // artık YOK. Mevcut dokümanlardaki alanlara dokunulmuyor, yalnız yeni yazım durdu.
  const handleTeslimNotlariConfirm = async (notlar) => {
    if (!secilenIsEmri) return;

    const workOrderRef = doc(db, "workOrders", secilenIsEmri.id);
    try {
      // G3: PPF Full Body de PPF SMS takibine dahildir (ppfFollowUp)
      const hasPPF = secilenIsEmri.uygulamalar?.ppf?.length > 0 || secilenIsEmri.uygulamalar?.ppfFullBody?.length > 0;

      const updateData = {
        durum: "Teslim Edildi",
        teslimNotlari: notlar,
        teslimEdilmeTarihi: serverTimestamp()
      };

      if (hasPPF) {
        const smsGonderimTarihi = new Date();
        smsGonderimTarihi.setDate(smsGonderimTarihi.getDate() + 14);
        smsGonderimTarihi.setHours(10, 0, 0, 0);
        updateData.ppfSmsTarihi = smsGonderimTarihi;
        updateData.ppfSmsGonderildi = false;
      }

      await updateDoc(workOrderRef, updateData);

      // Araç teslim edildiğinde bağlı randevu da kapanır (takvimdeki "Geciken"
      // hayaletlerin kaynağı: hiçbir kod yolu randevuya 'Tamamlandı' yazmıyordu).
      try {
        await closeLinkedAppointments(secilenIsEmri.id, secilenIsEmri.appointmentId);
      } catch (randevuHata) {
        console.error("Bağlı randevu kapatılamadı:", randevuHata);
      }

      // Yazdırma için state'i ayarla (TeslimFormu akışı aynen korunuyor)
      setOrderToPrint({
        ...secilenIsEmri,
        teslimNotlari: notlar
      });

      // Modalı kapat ve state'leri temizle
      setTeslimAkisAcik(false);
      setSecilenIsEmri(null);

    } catch (e) {
      console.error("Araç teslim edilirken hata oluştu: ", e);
      // Hata modalda gösterilsin diye yukarı fırlatılıyor: modal KAPANMAZ,
      // personel "teslim edildi" sanıp ekrandan çıkmaz.
      throw e;
    }
  };

  // Teslim akışı iptal
  const handleTeslimAkisIptal = () => {
    setTeslimAkisAcik(false);
    setSecilenIsEmri(null);
  };

  const getWorkOrdersByStatus = (status) => {
    return workOrders.filter(wo => wo.durum === status);
  };

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
  }

  return (
    <>
      {/* Teslim Akışı (tek adım) */}
      <TeslimNotlariModal
        open={teslimAkisAcik}
        onClose={handleTeslimAkisIptal}
        onConfirm={handleTeslimNotlariConfirm}
        aracPlaka={secilenIsEmri?.aracPlaka || ''}
      />

      {/* Yazdırma için gizli bileşen */}
      <Box sx={{ display: 'none' }}>
        <TeslimFormu ref={printRef} workOrder={orderToPrint} />
      </Box>

      {/* Garaj Kanban Board */}
      <Box sx={{ display: 'flex', gap: 3, width: '100%', overflowX: 'auto', height: 'calc(100vh - 150px)' }}>
        {statuses.map(status => (
          <Box key={status} sx={{ flex: '1 1 0', minWidth: 300, display: 'flex', flexDirection: 'column' }}>
            <StatusColumn
              title={status}
              workOrders={getWorkOrdersByStatus(status)}
              allStatuses={statuses}
              onStatusChange={handleStatusChange}
              onMarkAsDelivered={handleMarkAsDelivered}
            />
          </Box>
        ))}
      </Box>
    </>
  );
};

export default Garaj;